<script setup lang="ts">
import type { BaseTabProps } from "./Tab.vue";
import { type Slots, useSlots } from "vue";

export interface BaseTabsProps {
  rootClass?: string;
  activeIndex?: number | string;
}

const emit = defineEmits(["update:activeIndex"]);
const props = defineProps<BaseTabsProps>();

const slots: Slots = useSlots();
const filterTabs = (slot: any) => slot.type?.__name === "Tab";
const formatTab = (slot: any) => ({ ...(slot.props as BaseTabProps), slotBody: slot.children?.default });
const tabs = computed(() => (slots.default ? slots.default().filter(filterTabs).map(formatTab) : []));

const activeTabIndex = ref(Number(props.activeIndex) || 0);
const setActiveTabIndex = (index: number) => {
  activeTabIndex.value = index;
  emit("update:activeIndex", String(index));
};
</script>

<template>
  <div class="BaseTabs" :class="props.rootClass">
    <ul class="BaseTabs__list">
      <li
        v-for="(tab, index) in tabs" :key="index" class="BaseTabsList__item"
        :class="{ BaseTabsList_item_active: index === activeTabIndex }"
      >
        <div
          class="flex items-center justify-center gap-2 cursor-pointer"
          @click="setActiveTabIndex(index)"
        >
          <div :title="tab.heading">
            {{ tab.heading }}
          </div>

          <BaseButton
            v-if="tab.icon"
            type="icon"
            tag="nuxt-link"
            name="stats-link"
            :to="tab.icon.path ? { path: tab.icon.path } : {}"
          >
            <IconStats v-if="tab.icon.name === 'stats'" />
          </BaseButton>
        </div>
      </li>
    </ul>
  </div>
  <template v-for="(tab, tabIndex) in tabs" :key="`tab-${tabIndex}`">
    <component :is="tab.slotBody" v-if="tabIndex === activeTabIndex && tab.slotBody" />
  </template>
</template>

<style lang="scss">
.BaseTabs {
  @apply flex mb-[1.75rem];

  .BaseTabs__list {
    @apply flex-1 flex list-none p-1.5 text-center overflow-auto whitespace-nowrap;
    background: var(--base-tab-background-color);
    border-radius: var(--base-border-radius);

    .BaseTabsList__item {
      @apply w-full truncate px-4 py-1.5 font-medium cursor-pointer;
      color: var(--base-tab-text-color);
      border-radius: var(--base-border-radius);
    }

    .BaseTabsList_item_active {
      @apply font-bold bg-[var(--base-active-tab-color)];
      color: var(--base-tab-background-color);
    }
  }
}
</style>
